import { FocusEventHandler, memo, useCallback, useContext, useEffect, useId, useState } from "react";
import { NumberField, NumberFieldProps } from "../../../form";
import { SpreadsheetHighlightContext } from "../SpreadsheetHighlightContext";

import styles from "./style.module.scss";

export type SpreadsheetRowPickerProps = NumberFieldProps & {
  highlightColor?: string,
  highlightAlways?: boolean
  highlightOnFocus?: boolean
}

export const SpreadsheetRowPicker = memo<SpreadsheetRowPickerProps>((
  {
    highlightColor,
    value,
    children,
    onFocus,
    onBlur,
    highlightAlways = true,
    highlightOnFocus = false,
    ...props
  }) => {
  const { registerRow } = useContext(SpreadsheetHighlightContext);
  const id = useId();
  const [ isFocused, setIsFocused ] = useState(false);

  useEffect(() => {
    if (highlightColor && (highlightAlways || (highlightOnFocus && isFocused))) {
      registerRow(id, value, highlightColor);

      return () => {
        registerRow(id, null, highlightColor);
      };
    }
  }, [ id, value, highlightColor, highlightAlways, highlightOnFocus, isFocused, registerRow ]);

  const wrappedOnBlur: FocusEventHandler<HTMLInputElement> = useCallback(event => {
    setIsFocused(false);
    onBlur?.(event);
  }, [ onBlur ]);

  const wrappedOnFocus: FocusEventHandler<HTMLInputElement> = useCallback(event => {
    setIsFocused(true);
    onFocus?.(event);
  }, [ onFocus ]);

  return (
    <NumberField
      value={value}
      min={1}
      isClearable={false}
      onBlur={wrappedOnBlur}
      onFocus={wrappedOnFocus}
      {...props}
    >
      <div className={styles.marker} style={{ background: highlightColor }} />
      {children}
    </NumberField>
  );
});

export default SpreadsheetRowPicker;
